export type AnnouncementStatus = 'draft' | 'published' | 'archived'
export type AnnouncementReceiptFilter = 'unread' | 'unacked'

export interface AnnouncementType {
  id: number
  key: string
  label: string
  requires_ack: boolean
  priority: number
  required_permission?: string | null
  active: boolean
}

export interface AnnouncementUser {
  id: number
  name: string
  email: string
}

// Targeting rule set at authoring; the server resolves it to recipients at
// publish (dropping inactive/service/non-existent ids). v1 shapes:
//   { all: true } | { roles: [ids] } | { users: [ids] } | roles+users union
export interface AudienceRule {
  all?: boolean
  roles?: number[]
  users?: number[]
}

// The current viewer's own state for an announcement (null if they have no receipt).
export interface MyAnnouncementReceipt {
  delivered_at?: string | null
  read_at?: string | null
  acknowledged_at?: string | null
}

export interface Announcement {
  id: number
  type: AnnouncementType
  author?: AnnouncementUser | null
  title: string
  body: string
  audience_rule?: Record<string, unknown> | null
  status: AnnouncementStatus
  published_at?: string | null
  created_at: string
  updated_at: string
  myReceipt?: MyAnnouncementReceipt | null
}

export interface AnnouncementFeed {
  announcements: Announcement[]
  total: number
  unread_count: number
}

export interface AnnouncementReceiptRow {
  recipient: AnnouncementUser
  delivered_at: string
  read_at?: string | null
  acknowledged_at?: string | null
}

export interface AnnouncementReceiptSummary {
  total: number
  read: number
  acknowledged: number
}

export interface CreateAnnouncementInput {
  type_id: number
  title: string
  body: string
  audience_rule?: AudienceRule
}

export interface UpdateAnnouncementInput {
  type_id?: number
  title?: string
  body?: string
  audience_rule?: Record<string, unknown>
}

// ── UI helpers ────────────────────────────────────────────────────────────────

// A feed item is unread when the viewer has a receipt but hasn't read it.
// A null receipt is a valid "visible but no obligation" state (e.g. a new hire
// seeing announcements published before they joined) — not unread, not an error.
export const isUnread = (a: Announcement): boolean =>
  !!a.myReceipt && !a.myReceipt.read_at

// The Acknowledge action is only offered where the viewer actually has an
// obligation: a receipt exists, the type requires ack, and it isn't acked yet.
export const needsAcknowledgement = (a: Announcement): boolean =>
  !!a.myReceipt && a.type.requires_ack && !a.myReceipt.acknowledged_at
